import type { ReactElement } from 'react'
import type { ISensemakerOverviewMetrics } from '@brushfire/types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  SENSEMAKER_OVERVIEW_METRIC_ICON_RADIUS_PX,
  SENSEMAKER_OVERVIEW_METRIC_ICON_SIZE_PX,
  SENSEMAKER_OVERVIEW_METRIC_ICON_TO_VALUE_GAP_PX,
  SENSEMAKER_OVERVIEW_METRIC_TILE_BORDER,
  SENSEMAKER_OVERVIEW_METRIC_TILE_GAP_PX,
  SENSEMAKER_OVERVIEW_METRIC_TILE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_METRIC_TILE_PADDING_PX,
  SENSEMAKER_OVERVIEW_METRIC_TILE_RADIUS_PX,
  SENSEMAKER_OVERVIEW_METRIC_VALUE_TO_LABEL_GAP_PX,
} from '../../lib/sensemaker-overview-layout'
import { formatOverviewUsd } from '../../lib/sensemaker-overview-format'
import {
  OverviewDollarCircleMetricIcon,
  OverviewFileTextIcon,
  OverviewMegaphoneIcon,
  OverviewWalletIcon,
} from './SensemakerOverviewMetricIcons'

type MetricKey = keyof ISensemakerOverviewMetrics

interface TileConfig {
  key: MetricKey
  label: string
  cardBg: string
  iconBg: string
  labelColor: string
  icon: ReactElement
  format: 'count' | 'usd'
}

const TILES: TileConfig[] = [
  {
    key: 'totalCampaigns',
    label: 'Total Campaigns',
    cardBg: '#daf5e4',
    iconBg: '#3f8367',
    labelColor: '#3f8367',
    icon: <OverviewMegaphoneIcon />,
    format: 'count',
  },
  {
    key: 'totalInstruments',
    label: 'Total Instruments',
    cardBg: '#fae6dc',
    iconBg: '#df6534',
    labelColor: '#df6534',
    icon: <OverviewFileTextIcon />,
    format: 'count',
  },
  {
    key: 'totalDistributedUsd',
    label: 'Total Distributed Amount',
    cardBg: '#fae3ed',
    iconBg: '#ff5959',
    labelColor: '#ff5959',
    icon: <OverviewDollarCircleMetricIcon />,
    format: 'usd',
  },
  {
    key: 'totalBilledUsd',
    label: 'Total Billed Amount',
    cardBg: '#c7ebf9',
    iconBg: '#0c9eeb',
    labelColor: '#0c9eeb',
    icon: <OverviewWalletIcon />,
    format: 'usd',
  },
]

function formatMetricValue(raw: number, format: TileConfig['format']): string {
  if (format === 'usd') {
    return formatOverviewUsd(raw)
  }
  return raw.toLocaleString('en-US')
}

interface Props {
  metrics: ISensemakerOverviewMetrics
}

export function SensemakerOverviewMetricTiles({ metrics }: Readonly<Props>) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: `${SENSEMAKER_OVERVIEW_METRIC_TILE_GAP_PX}px`,
      }}
      data-testid="sensemaker-overview-metric-tiles"
    >
      {TILES.map((tile) => {
        const value = formatMetricValue(metrics[tile.key], tile.format)

        return (
          <Box
            key={tile.key}
            data-testid={`sensemaker-overview-metric-${tile.key}`}
            sx={{
              bgcolor: tile.cardBg,
              border: SENSEMAKER_OVERVIEW_METRIC_TILE_BORDER,
              borderRadius: `${SENSEMAKER_OVERVIEW_METRIC_TILE_RADIUS_PX}px`,
              height: SENSEMAKER_OVERVIEW_METRIC_TILE_HEIGHT_PX,
              boxSizing: 'border-box',
              p: `${SENSEMAKER_OVERVIEW_METRIC_TILE_PADDING_PX}px`,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                width: SENSEMAKER_OVERVIEW_METRIC_ICON_SIZE_PX,
                height: SENSEMAKER_OVERVIEW_METRIC_ICON_SIZE_PX,
                borderRadius: `${SENSEMAKER_OVERVIEW_METRIC_ICON_RADIUS_PX}px`,
                bgcolor: tile.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {tile.icon}
            </Box>

            <Typography
              sx={{
                mt: `${SENSEMAKER_OVERVIEW_METRIC_ICON_TO_VALUE_GAP_PX}px`,
                fontSize: '30px',
                fontWeight: 700,
                lineHeight: '36px',
                letterSpacing: '0.3955px',
                color: '#0f172a',
              }}
            >
              {value}
            </Typography>

            <Typography
              sx={{
                mt: `${SENSEMAKER_OVERVIEW_METRIC_VALUE_TO_LABEL_GAP_PX}px`,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                letterSpacing: '-0.1504px',
                color: tile.labelColor,
              }}
            >
              {tile.label}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}
